import { useState, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import LoadingButton from '../../Components/LoadingButton';
import Modal from '../../Components/Modal';
import Table from '../../Components/Table';
import FormSwitch from '../../Components/forms/FormSwitch';
import FormTextbox from '../../Components/forms/FormTextbox';

import { ContentContainer, ContentBox, TableBox } from '../../theme';
import {
  getDepartments,
  createDepartment,
  editDepartment,
} from '../../api/services';
import { useModal } from '../../hooks';
import { DEPARTMENT_COLUMNS } from '../../constants/tables/columns';
import { DEPARTMENT_INITIAL_VALUES } from '../../constants/forms/initialValues';
import {
  DEPARTMENT_EDIT_SCHEMA,
  DEPARTMENT_ADD_SCHEMA,
} from '../../constants/forms/schemas';
import useAuth from '../../context/useAuth';

const Departments = () => {
  const [departments, setDepartments] = useState(null);
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const { setUser } = useAuth();

  const [open, setOpen] = useModal({ ref: false });

  const fetchData = useCallback(async () => {
    try {
      const res = await getDepartments();
      setDepartments(res);
      setLoading(false);
    } catch (error) {
      if (error.status === 401) setUser(null);
      toast.error(error.message);
    }
  }, [setUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRequest = data => {
    setEditData(data.row);
    setOpen(true);
  };

  const handleOpen = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  return (
    <>
      <ContentContainer maxWidth="xl">
        <ContentBox>
          <TableBox>
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                disabled={loading}
                endIcon={<AddIcon />}
                onClick={handleOpen}
                variant="contained"
              >
                Nuevo
              </Button>
            </Box>

            <Box sx={{ height: '100%' }}>
              <Table
                columns={DEPARTMENT_COLUMNS(handleEditRequest)}
                loading={loading}
                rows={departments}
              />
            </Box>
          </TableBox>
        </ContentBox>
      </ContentContainer>

      {/* New/edit Modal */}
      <Modal
        disabled={loading}
        open={open}
        onClose={handleClose}
        title={`${editData ? 'Editar' : 'Nuevo'} departamento`}
        width="sm"
      >
        <Formik
          initialValues={editData || DEPARTMENT_INITIAL_VALUES}
          onSubmit={async values => {
            try {
              setLoading(true);

              editData
                ? await editDepartment(values)
                : await createDepartment(values);

              toast.success(
                `Departamento ${editData ? 'editado' : 'creado'} con éxito.`
              );

              await fetchData();
              handleClose();
            } catch (error) {
              setLoading(false);

              if (error.status === 409) {
                setError(error.message);
                return;
              }

              if (error.status === 401) setUser(null);
              toast.error(error.message);
            }
          }}
          validationSchema={
            editData ? DEPARTMENT_EDIT_SCHEMA : DEPARTMENT_ADD_SCHEMA
          }
        >
          {props => (
            <Form>
              <Grid container spacing={2} sx={{ p: 2, mb: 4 }}>
                {error && (
                  <Grid item xs={12}>
                    <Alert
                      onClose={() => setError(null)}
                      severity="error"
                      sx={{
                        mb: 2,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'error.dark',
                      }}
                    >
                      {error}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormTextbox
                    disabled={loading}
                    label="Departamento"
                    name="departmentName"
                  />
                </Grid>

                {editData && (
                  <Grid item xs={12}>
                    <FormSwitch
                      disabled={loading}
                      name="isActive"
                      onLabel="Activo"
                      offLabel="Inactivo"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Buttons */}
              <Box
                sx={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  width: '100%',
                  px: 2,
                }}
              >
                <Button color="error" disabled={loading} onClick={handleClose}>
                  Cancelar
                </Button>
                <LoadingButton
                  icon={editData ? <EditIcon /> : <AddIcon />}
                  loading={loading}
                  text={editData ? 'Editar' : 'Guardar'}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default Departments;
